import { generateUserId } from '../utils/ids';

// AWS backend (Base44 replacement)
// CRITICAL: Always use AWS in production with hardcoded fallback
const PRODUCTION_API_URL = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
const isProductionMode = () => {
  return window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
};

const AWS_API_BASE_URL = isProductionMode() 
  ? PRODUCTION_API_URL 
  : (import.meta?.env?.VITE_API_BASE_URL || PRODUCTION_API_URL).trim();

const hasAwsBackend = () => {
  // In production, ALWAYS return true (force AWS mode)
  if (isProductionMode()) return true;
  // In dev, check if env var is set
  return !!AWS_API_BASE_URL;
};

const awsUrl = (path) => {
  const base = AWS_API_BASE_URL.replace(/\/+$/, '');
  const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  return `${base}${p}`;
};
const awsJson = async (path, { method = 'GET', body } = {}) => {
  const url = awsUrl(path);
  console.log(`[AWS API] ${method} ${url}`);
  
  try {
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    
    const data = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      const msg = data?.error || data?.message || `API Error: ${res.status} ${res.statusText}`;
      console.error('[AWS API] Error:', msg, data);
      throw new Error(msg);
    }
    
    console.log('[AWS API] Success:', res.status);
    return data;
  } catch (error) {
    console.error('[AWS API] Network/Parse Error:', error);
    throw error;
  }
};

// Local Storage User Management (fallback when API User entity doesn't exist)
const getLocalUsers = () => {
  const users = localStorage.getItem('localUsers');
  return users ? JSON.parse(users) : [];
};

const saveLocalUsers = (users) => {
  localStorage.setItem('localUsers', JSON.stringify(users));
};

// Persist a stable user_id for an email (one-time generation, reversible by clearing this key)
const USER_ID_BY_EMAIL_KEY = 'userIdByEmail';
const normalizeEmail = (email) => (email ? String(email).trim().toLowerCase() : '');

// Best-effort: infer a previously-used user_id for this email from local resumes stored in this browser.
// This helps "restore" profiles that appear missing due to historical user_id instability.
const tryInferUserIdFromLocalResumes = (email) => {
  const target = normalizeEmail(email);
  if (!target) return null;

  const extractEmails = (r) => {
    const vals = [
      r?.contact_email,
      r?.contactEmail,
      r?.user_email,
      r?.userEmail,
      r?.email,
      r?.owner_email,
      r?.ownerEmail
    ].filter(Boolean);
    return vals.map(normalizeEmail).filter(Boolean);
  };

  const getTs = (r) => {
    const raw =
      r?.updated_at ??
      r?.updatedAt ??
      r?.created_at ??
      r?.createdAt ??
      r?.updated ??
      r?.created;
    const t = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  const candidates = new Map(); // userId -> { count, latestTs }

  const consider = (userId, resumes) => {
    if (!userId || !Array.isArray(resumes)) return;
    let count = 0;
    let latestTs = 0;
    for (const r of resumes) {
      const emails = extractEmails(r);
      if (emails.includes(target)) {
        count += 1;
        latestTs = Math.max(latestTs, getTs(r));
      }
    }
    if (count <= 0) return;
    const prev = candidates.get(userId);
    if (!prev || count > prev.count || (count === prev.count && latestTs > prev.latestTs)) {
      candidates.set(userId, { count, latestTs });
    }
  };

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      if (k.startsWith('localResumes:')) {
        const userId = k.slice('localResumes:'.length);
        try {
          const arr = JSON.parse(localStorage.getItem(k) || '[]');
          consider(userId, arr);
        } catch {
          // ignore
        }
      } else if (k.startsWith('localResumesBackup:')) {
        const userId = k.slice('localResumesBackup:'.length);
        try {
          const obj = JSON.parse(localStorage.getItem(k) || '{}');
          consider(userId, obj?.resumes);
        } catch {
          // ignore
        }
      }
    }
  } catch {
    // ignore (private mode, quota, etc.)
  }

  if (candidates.size === 0) return null;

  // Pick the "best" bucket by count, then recency.
  let bestUserId = null;
  let best = { count: -1, latestTs: -1 };
  for (const [userId, meta] of candidates.entries()) {
    if (meta.count > best.count || (meta.count === best.count && meta.latestTs > best.latestTs)) {
      bestUserId = userId;
      best = meta;
    }
  }
  return bestUserId;
};

const getLocalResumeCountForUserId = (userId) => {
  if (!userId) return 0;
  try {
    const raw = localStorage.getItem(`localResumes:${userId}`);
    if (!raw) return 0;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.length : 0;
  } catch {
    return 0;
  }
};

const getUserIdByEmailMap = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_ID_BY_EMAIL_KEY) || '{}');
  } catch {
    return {};
  }
};
const saveUserIdByEmailMap = (map) => {
  try {
    localStorage.setItem(USER_ID_BY_EMAIL_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};
const ensureStableUserIdForEmail = (email, preferredUserId) => {
  const key = normalizeEmail(email);
  if (!key) return preferredUserId || null;
  if (preferredUserId) {
    const map = getUserIdByEmailMap();
    if (!map[key]) {
      map[key] = preferredUserId;
      saveUserIdByEmailMap(map);
    }
    return preferredUserId;
  }
  const map = getUserIdByEmailMap();
  if (map[key]) {
    // If the mapped id has no local data, but another local bucket clearly matches this email,
    // repair the mapping to point at the bucket that contains the profile.
    const existing = map[key];
    const existingCount = getLocalResumeCountForUserId(existing);
    if (existingCount > 0) return existing;

    const inferred = tryInferUserIdFromLocalResumes(email);
    if (inferred && inferred !== existing) {
      map[key] = inferred;
      saveUserIdByEmailMap(map);
      return inferred;
    }
    return existing;
  }

  const inferred = tryInferUserIdFromLocalResumes(email);
  if (inferred) {
    map[key] = inferred;
    saveUserIdByEmailMap(map);
    return inferred;
  }
  const created = generateUserId();
  map[key] = created;
  saveUserIdByEmailMap(map);
  return created;
};

const addLocalUser = (userData) => {
  const users = getLocalUsers();
  // Check if user already exists
  if (users.find(u => u.email === userData.email)) {
    throw new Error('User with this email already exists');
  }
  
  const newUser = {
    id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    user_id: ensureStableUserIdForEmail(userData.email, userData.user_id), // immutable UUID (primary identifier)
    ...userData,
    createdAt: new Date().toISOString()
  };
  
  users.push(newUser);
  saveLocalUsers(users);
  return newUser;
};

// Ensure a local user has a stable user_id (non-destructive, per-user)
const ensureLocalUserId = (email) => {
  if (!email) return null;
  const users = getLocalUsers();
  const idx = users.findIndex(u => u.email === email);
  if (idx === -1) return null;
  if (!users[idx].user_id) {
    users[idx] = { ...users[idx], user_id: ensureStableUserIdForEmail(email) };
    saveLocalUsers(users);
  }
  return users[idx].user_id;
};

// Normalize a user session object to always include immutable user_id
const toUserSession = (u) => {
  // If backend doesn't provide a UUID, we still keep a stable one (per-email map).
  // IMPORTANT: do NOT treat backend entity `id` as `user_id` (different namespace).
  const userId = ensureStableUserIdForEmail(u.email, u.user_id || u.userId || null);
  return {
    ...u,
    id: u.id || u._id || u.id,
    user_id: userId,
    email: u.email,
    name: u.name || u.email,
    // Preserve tokens from AWS Cognito login
    tokens: u.tokens || null,
    idToken: u.idToken || u.tokens?.idToken || null,
    accessToken: u.accessToken || u.tokens?.accessToken || null,
    refreshToken: u.refreshToken || u.tokens?.refreshToken || null
  };
};

// Auth API Service
export const authAPI = {
  /**
   * Register a new user
   */
  async register(userData) {
    try {
      // PRODUCTION: ALWAYS use AWS (no local fallback)
      if (isProductionMode()) {
        console.log('[Auth] Production mode - using AWS registration');
        const out = await awsJson('/auth/register', {
          method: 'POST',
          body: { name: userData?.name || '', email: userData?.email || '', password: userData?.password || '' }
        });
        const user = out?.user || null;
        const tokens = out?.tokens || null;
        if (!user?.email) throw new Error('Registration failed');
        const user_id = ensureStableUserIdForEmail(user.email, user.user_id || generateUserId());
        const userSession = toUserSession({ ...user, user_id, tokens });
        localStorage.setItem('user', JSON.stringify(userSession));
        console.log('[Auth] ✅ User registered and saved to AWS:', user.email);
        return userSession;
      }

      // DEV MODE: Try AWS first, fallback to local
      if (hasAwsBackend()) {
        const out = await awsJson('/auth/register', {
          method: 'POST',
          body: { name: userData?.name || '', email: userData?.email || '', password: userData?.password || '' }
        });
        const user = out?.user || null;
        const tokens = out?.tokens || null;
        if (!user?.email) throw new Error('Registration failed');
        const user_id = ensureStableUserIdForEmail(user.email, user.user_id || generateUserId());
        const userSession = toUserSession({ ...user, user_id, tokens });
        localStorage.setItem('user', JSON.stringify(userSession));
        return userSession;
      }

      // Fallback: local-only (dev only)
      return addLocalUser({ ...userData, user_id: userData.user_id || generateUserId() });
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  },

  /**
   * Login user
   */
  async login(email, password) {
    try {
      // PRODUCTION: ALWAYS use AWS (no local fallback)
      if (isProductionMode()) {
        console.log('[Auth] Production mode - using AWS login for:', email);
        console.log('[Auth] Using API URL:', AWS_API_BASE_URL);
        
        const out = await awsJson('/auth/login', {
          method: 'POST',
          body: { email, password }
        });
        
        const user = out?.user || null;
        const tokens = out?.tokens || null;
        
        console.log('[Auth] Login response received. Has user?', !!user, 'Has tokens?', !!tokens);
        
        if (!user?.email) throw new Error('Invalid email or password');
        if (!tokens?.idToken) {
          console.warn('[Auth] Login succeeded but no idToken received from backend');
        }
        
        const user_id = ensureStableUserIdForEmail(user.email, user.user_id || generateUserId());
        const userSession = toUserSession({ ...user, user_id, tokens });
        localStorage.setItem('user', JSON.stringify(userSession));
        
        console.log('[Auth] ✅ User logged in from AWS:', user.email);
        console.log('[Auth] Session saved. Token present:', !!userSession.idToken);
        
        return userSession;
      }

      // DEV MODE: Try AWS first, fallback to local
      if (hasAwsBackend()) {
        console.log('[Auth] Dev mode - using AWS login for:', email);
        console.log('[Auth] Using API URL:', AWS_API_BASE_URL);
        
        const out = await awsJson('/auth/login', {
          method: 'POST',
          body: { email, password }
        });
        
        const user = out?.user || null;
        const tokens = out?.tokens || null;
        
        console.log('[Auth] Login response received. Has user?', !!user, 'Has tokens?', !!tokens);
        
        if (!user?.email) throw new Error('Invalid email or password');
        if (!tokens?.idToken) {
          console.warn('[Auth] Login succeeded but no idToken received from backend');
        }
        
        const user_id = ensureStableUserIdForEmail(user.email, user.user_id || generateUserId());
        const userSession = toUserSession({ ...user, user_id, tokens });
        localStorage.setItem('user', JSON.stringify(userSession));
        
        // Debug: verify token was stored
        if (import.meta?.env?.DEV) {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          console.log('[Auth] Token stored?', !!stored?.tokens?.idToken, 'Token length:', stored?.tokens?.idToken?.length || 0);
        }
        
        return userSession;
      }

      // Fallback: local-only auth (dev only)
      console.log('[Auth] Fallback to local auth for:', email);
      const localUsers = getLocalUsers();
      const user = localUsers.find(u => u.email === email && u.password === password);
      if (!user) throw new Error('Invalid email or password');
      const user_id = ensureStableUserIdForEmail(email, ensureLocalUserId(email) || user.user_id);
      const userSession = toUserSession({ ...user, user_id });
      localStorage.setItem('user', JSON.stringify(userSession));
      return userSession;
    } catch (error) {
      console.error('[Auth] Login error details:', {
        message: error.message,
        email: email,
        isProduction: isProductionMode(),
        apiUrl: AWS_API_BASE_URL
      });
      throw error;
    }
  },

  /**
   * Logout user
   */
  logout() {
    // DON'T clear the persistent backup!
    const PERSISTENT_KEY = 'benjamin_madera_persistent_backup';
    const backup = localStorage.getItem(PERSISTENT_KEY);
    
    // Only remove the current user session
    localStorage.removeItem('user');
    
    // Restore the persistent backup immediately
    if (backup) {
      localStorage.setItem(PERSISTENT_KEY, backup);
    }
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('user');
  },

  /**
   * Request password reset - sends reset token to user's email
   */
  async requestPasswordReset(email) {
    try {
      // Use AWS backend with fallback
      const apiUrl = AWS_API_BASE_URL || 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      
      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        return { success: true, message: 'If an account exists with this email, a reset code has been sent.' };
      }
      
      // Fallback to local mode if AWS fails
      if (hasAwsBackend()) {
        await awsJson('/auth/forgot-password', { method: 'POST', body: { email } });
        return { success: true, message: 'If an account exists with this email, a reset code has been sent.' };
      }

      // Generate reset token
      const resetToken = `reset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const resetExpiry = Date.now() + (60 * 60 * 1000); // 1 hour from now
      
      // Store reset token in localStorage
      const resetTokens = JSON.parse(localStorage.getItem('passwordResetTokens') || '{}');
      resetTokens[resetToken] = {
        email: email,
        expiresAt: resetExpiry
      };
      localStorage.setItem('passwordResetTokens', JSON.stringify(resetTokens));

      // In a real app, you would send an email here via Base44's email service or external service
      // For now, we'll log it (in production, this would be sent via email service)
      console.log(`Password reset link: ${window.location.origin}/reset-password?token=${resetToken}`);
      
      return { success: true, message: 'If an account exists with this email, a password reset link has been sent.', token: resetToken };
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  },

  /**
   * Reset password using token
   */
  async resetPassword(token, newPassword) {
    try {
      // AWS flow uses "code + email" instead of a tokenized link.
      // See confirmForgotPassword(email, code, newPassword).
      if (hasAwsBackend()) {
        throw new Error('Use email + code to reset password.');
      }

      // Get reset token from localStorage
      const resetTokens = JSON.parse(localStorage.getItem('passwordResetTokens') || '{}');
      const resetData = resetTokens[token];

      if (!resetData) {
        throw new Error('Invalid or expired reset token');
      }

      if (Date.now() > resetData.expiresAt) {
        // Remove expired token
        delete resetTokens[token];
        localStorage.setItem('passwordResetTokens', JSON.stringify(resetTokens));
        throw new Error('Reset token has expired. Please request a new password reset.');
      }

      const email = resetData.email;

      // Fallback to localStorage
      const localUsers = getLocalUsers();
      const userIndex = localUsers.findIndex(u => u.email === email);
      
      if (userIndex === -1) {
        throw new Error('User not found');
      }

      // Update password
      localUsers[userIndex].password = newPassword;
      saveLocalUsers(localUsers);

      // Remove used token
      delete resetTokens[token];
      localStorage.setItem('passwordResetTokens', JSON.stringify(resetTokens));

      return { success: true, message: 'Password has been reset successfully.' };
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  },

  /**
   * AWS/Cognito password reset confirm (email + code + newPassword)
   */
  async confirmForgotPassword(email, code, newPassword) {
    // Use AWS backend with fallback
    const apiUrl = AWS_API_BASE_URL || 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
    
    const res = await fetch(`${apiUrl}/auth/confirm-forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, newPassword })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.error || data.message || 'Failed to reset password');
    }
    
    return { success: true, message: 'Password has been reset successfully.' };
  }
};

export default authAPI;
